import {HttpErrorResponse} from '@angular/common/http';
import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, EMPTY} from 'rxjs';
import {catchError, takeUntil, tap} from 'rxjs/operators';

import {ToastrService} from 'ngx-toastr';
import {RegulationTreeService} from '../../+configuration/regulation-tree/regulation-tree.service';
import {Destroyable} from '../../destroyable.component';
import {CliReg} from '../../models/models.interfaces';
import {Data} from '../../shared/api';
import {Validators as GpValidators} from '../../shared/forms';
import {I18nManager} from '../../shared/i18n/i18n.module';
import {ModalService} from '../../shared/modal';
import {TreeNode} from '../../shared/ui';
import {ETemplate} from '../template-management.interfaces';
import {TemplateManagementService} from '../template-management.service';


@Component({
  selector: 'gp-template-action-modal',
  templateUrl: './template-action-modal.component.html',
  styleUrls: ['./template-action-modal.component.scss']
})
export class TemplateActionModalComponent extends Destroyable implements OnInit {
  public templateCreated: (id: number) => void;
  public createNewFolderToggle = false;
  public regulationTree: Array<TreeNode<CliReg>> = null;
  public errorMessage: string;
  public templateForm: UntypedFormGroup;
  public languages: { [key: string]: string } = {};
  public folderNames: BehaviorSubject<string[]> = new BehaviorSubject(null);
  public filteredFolderNames: string[] = [];
  public regulations: Array<Data<CliReg>>;
  public selectedTemplate: ETemplate;
  public createTemplate = false;
  public editTemplate = false;
  public duplicateTemplate = false;

  @ViewChild('searchInput')
  public searchInputElem: ElementRef;
  @ViewChild('newFolderInput')
  public newFolderInput: ElementRef;


  constructor(
    private templateManagementService: TemplateManagementService,
    private regulationTreeService: RegulationTreeService,
    private translate: TranslateService,
    private toastService: ToastrService,
    private formBuilder: UntypedFormBuilder,
    private i18nManager: I18nManager,
    private modalService: ModalService
  ) {
    super();
    this.templateForm = this.formBuilder.group({
      cliRegId: ['', Validators.required],
      locale: ['', Validators.required],
      name: ['', [Validators.required, Validators.maxLength(50), GpValidators.noWhitespaceValidator]],
      folder: ['', Validators.required]
    });
  }

  public ngOnInit(): void {
    this.languages = this.i18nManager.availableLanguages;
    this.regulationTree = this.regulationTreeService.createTree(this.regulations);
    this.templateManagementService.getFolderNames()
      .pipe(
        tap(response => {
          this.folderNames.next(response);
          this.filteredFolderNames = this.folderNames.getValue();
          if (this.editTemplate || this.duplicateTemplate) {
            this.templateForm.get('cliRegId').setValue(this.selectedTemplate.cliRegId);
            this.templateForm.get('name').setValue(this.selectedTemplate.name);
            this.templateForm.get('locale').setValue(this.selectedTemplate.locale);
            setTimeout(() => this.templateForm.get('folder').setValue(this.selectedTemplate.folder), 0);
          }
        }),
        takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  public onSubmit(): void {
    if (this.templateForm.valid) {
      if (this.editTemplate) {
        this.updateETemplate();
      } else if (this.duplicateTemplate) {
        this.duplicateETemplate();
      } else {
        this.createETemplate();
      }
    }
  }

  public createETemplate(): void {
    this.templateManagementService.createNewETemplate(this.templateForm.value, this.getGloRegId())
      .pipe(
        tap((result) => {
          this.modalService.closeTopmostModal();
          this.templateCreated(result.id);
          this.toastService.success(this.translate.instant(`TEMPLATE.created_successfully`));
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          this.errorMessage = `${this.translate.instant(`TEMPLATE.creation_failed`)}:
            ${errorResponse?.error?.message || errorResponse?.error?.description}`;
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public updateETemplate(): void {
    this.selectedTemplate.name = this.templateForm.get('name').value;
    this.selectedTemplate.folder = this.templateForm.get('folder').value;
    this.selectedTemplate.locale = this.templateForm.get('locale').value;
    this.templateManagementService.updateETemplate(this.selectedTemplate)
      .pipe(
        tap(() => {
          this.modalService.closeTopmostModal();
          this.toastService.success(this.translate.instant(`TEMPLATE.settings_modified_successfully`));
          this.templateCreated(this.selectedTemplate.id);
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          this.errorMessage = `${this.translate.instant(`TEMPLATE.settings_modification_failed`)}:
            ${errorResponse?.error?.message || errorResponse?.error?.description}`;
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public duplicateETemplate(): void {
    const duplicatedTemplate: ETemplate = {...this.selectedTemplate};
    duplicatedTemplate.name = this.templateForm.get('name').value;
    duplicatedTemplate.folder = this.templateForm.get('folder').value;
    duplicatedTemplate.locale = this.templateForm.get('locale').value;
    duplicatedTemplate.availability = [];
    this.templateManagementService.duplicateETemplate(duplicatedTemplate, this.getGloRegId())
      .pipe(
        tap((result) => {
          this.modalService.closeTopmostModal();
          this.templateCreated(result.id);
          this.toastService.success(this.translate.instant(`TEMPLATE.created_successfully`));
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          this.errorMessage = `${this.translate.instant(`TEMPLATE.creation_failed`)}:
            ${errorResponse?.error?.message || errorResponse?.error?.description}`;
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public filterFolderNames(value: string): void {
    this.createNewFolderToggle = false;
    const filterText = value.toLowerCase();
    this.filteredFolderNames = this.folderNames.getValue()
      .filter(folderName => folderName !== null && (folderName.toLowerCase().indexOf(filterText) !== -1 || !filterText));
  }

  public toggleNewFolderCreate(): void {
    this.createNewFolderToggle = true;
    this.filteredFolderNames = this.folderNames.getValue();
    let newFolderNameValue = '';
    const searchInputValue = this.searchInputElem.nativeElement.value.trim();
    if (searchInputValue !== '' && !(this.isFolderNameExists(searchInputValue))) {
      newFolderNameValue = searchInputValue;
      this.searchInputElem.nativeElement.value = '';
    }
    setTimeout(() => {
      this.newFolderInput.nativeElement.value = newFolderNameValue;
      this.newFolderInput.nativeElement.focus();
    });
  }

  public addNewFolder(): void {
    this.createNewFolderToggle = false;
    const newFolderNameValue = this.newFolderInput.nativeElement.value.trim();
    if (newFolderNameValue !== '') {
      if (!this.isFolderNameExists(newFolderNameValue)) {
        this.filteredFolderNames.push(newFolderNameValue);
        this.filteredFolderNames.sort((folder1, folder2) => folder1.localeCompare(folder2, undefined, {sensitivity: 'base'}));
      }
      setTimeout(() => {
        this.templateForm.get('folder').setValue(newFolderNameValue);
        this.templateForm.markAsDirty();
      }, 100);
    }
  }

  public resetFilterTextAndFolderNamesValues(): void {
    this.createNewFolderToggle = false;
    this.searchInputElem.nativeElement.value = '';
    this.filteredFolderNames = this.folderNames.getValue();
  }

  public isTemplateFormInValid(): boolean {
    if (this.templateForm?.invalid) {
      return true;
    }
    if (this.duplicateTemplate && this.templateForm.get('name').pristine && this.templateForm.get('locale').pristine
        && this.templateForm.get('cliRegId').pristine) {
      return true;
    }
    return this.templateForm?.pristine;
  }

  private getGloRegId(): number {
    const cliRegId = this.templateForm.get('cliRegId').value;
    const selected: Data<CliReg> = this.regulations?.find((r: Data<CliReg>) => r.id === cliRegId);
    return selected?.attributes.gloReg;
  }

  private isFolderNameExists(newFolderName: string): boolean {
    return this.filteredFolderNames.some(element => {
      return element.toLowerCase() === newFolderName.toLowerCase();
    });
  }
}

